import React, { useState, useEffect, useCallback, useRef } from 'react';
import { serverPHP } from '../const/consts';
import SyntheseSession from './SyntheseSession';

const Evaluation = ({ 
  user, 
  phase, 
  sujet, 
  setShowModal, 
  setModalTitle, 
  setModalContent, 
  setEndSession, 
  goNext, 
  closeSession, 
  parametres, 
  type, 
  redactionLongue 
}) => {
  const [currentContributions, setCurrentContributions] = useState([]);
  const [pastContributions, setPastContributions] = useState([]);
  const [currentContributionIndex, setCurrentContributionIndex] = useState(0);
  const [userEvaluations, setUserEvaluations] = useState(new Map());
  const [showAllContributions, setShowAllContributions] = useState(false);
  const isEvaluating = useRef(new Set()); // Pour suivre les évaluations en cours par ID
  const hasLoadedContributions = useRef(false); // Pour éviter les rechargements multiples

  const shuffleArray = (array) => {
    return array
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  };

  useEffect(() => {
    console.log('📊 Evaluation component mounted/updated:', {
      phase,
      userRole: user.role,
      userId: user.id,
      sessionId: user.currentsession
    });

    // Éviter de recharger les contributions si déjà chargées pour cette phase
    if (hasLoadedContributions.current) {
      console.log('📊 Contributions already loaded for this phase, skipping fetch');
      return;
    }

    console.log('📊 Fetching contributions for evaluation phase:', phase);
    
    fetch(`${serverPHP}getContributions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        session_id: user.currentsession, 
        phase: phase, 
        user_id: user.id, 
        action: "evaluation" 
      })
    })
      .then(response => response.json())
      .then(data => {
        console.log('📊 Received evaluation data:', {
          success: data.success,
          currentContributionsCount: data.current_contributions?.length || 0,
          pastContributionsCount: data.past_contributions?.length || 0
        });

        if (!data.success) {
          console.error("📊 Error in fetching contributions:", data);
          return;
        }

        // Marquer que les contributions ont été chargées
        hasLoadedContributions.current = true;

        // Si on n'a pas encore de contributions chargées, les mélanger
        if (currentContributions.length === 0) {
          const shuffledContributions = shuffleArray(data.current_contributions);
          console.log('📊 Shuffled contributions:', shuffledContributions.length);
          setCurrentContributions(shuffledContributions);
        }
        
        setPastContributions(data.past_contributions);
        
        // Si il n'y a qu'une seule contribution, passer à la suite automatiquement
        if (data.current_contributions.length === 1) {
          console.log('📊 Only one contribution, auto-advancing');
          goNext();
          return;
        }

        // Construire la map des évaluations utilisateur
        const evaluationsMap = new Map();
        data.current_contributions.forEach(contribution => {
          let valeurInterne = null;
          if (contribution.valeur !== null) {
            valeurInterne = contribution.valeur === 0 ? 5 : contribution.valeur;
          }
          evaluationsMap.set(contribution.id, valeurInterne);
        });
        
        console.log('📊 User evaluations map:', Array.from(evaluationsMap.entries()));
        setUserEvaluations(evaluationsMap);

        // NOUVEAU : Log crucial pour le débogage du podium
        console.log('✅ USER ACTIVELY PARTICIPATING IN EVALUATION PHASE:', {
          phase,
          userId: user.id,
          userRole: user.role,
          contributionsToEvaluate: data.current_contributions.length,
          timestamp: new Date().toISOString()
        });
      })
      .catch(error => {
        console.error('📊 Failed to fetch contributions:', error);
        hasLoadedContributions.current = false; // Permettre de réessayer en cas d'erreur
      });
  }, [user.currentsession, phase, goNext, closeSession]);

  // Reset du flag de chargement quand la phase change
  useEffect(() => {
    hasLoadedContributions.current = false;
  }, [phase]);

  const handleEvaluation = async (id, newValeur) => {
    if (isEvaluating.current.has(id)) return;

    const currentValeur = userEvaluations.get(id) || null;
    let valeurApresClic = currentValeur === newValeur ? null : newValeur;
    
    console.log('📊 Handling evaluation:', {
      contributionId: id,
      oldValue: currentValeur,
      newValue: valeurApresClic,
      phase,
      userId: user.id
    });
    
    const newEvaluations = new Map(userEvaluations);
    newEvaluations.set(id, valeurApresClic);
    setUserEvaluations(newEvaluations);
    
    isEvaluating.current.add(id);

    try {
      const valeurEnvoyee = valeurApresClic === 5 ? 0 : valeurApresClic;
      const response = await fetch(`${serverPHP}setEvaluation.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: id, user_id: user.id, valeur: valeurEnvoyee })
      });

      const data = await response.json();
      
      if (!data.success) throw new Error(data.error || "L'évaluation a échoué");

      console.log('✅ Evaluation saved successfully:', {
        contributionId: id,
        value: valeurEnvoyee,
        userId: user.id,
        phase
      });

    } catch (error) {
      console.error('📊 Failed to set evaluation:', error);
      
      // Rollback en cas d'erreur
      const revertedEvaluations = new Map(userEvaluations);
      revertedEvaluations.set(id, currentValeur);
      setUserEvaluations(revertedEvaluations);

      setModalContent(<div className="text-center">Erreur lors de l'enregistrement de votre vote. Veuillez réessayer.</div>);
      setModalTitle("❌ Erreur");
      setShowModal(true);
    } finally {
      isEvaluating.current.delete(id);
    }
  };

  const checkAllEvaluated = useCallback(() => {
    const evaluatedCount = Array.from(userEvaluations.values()).filter(value => value !== null).length;
    const totalContributions = currentContributions.length;
    const isReady = (evaluatedCount === totalContributions && totalContributions > 0);
    
    console.log('📊 Checking evaluation progress:', {
      evaluatedCount,
      totalContributions,
      isReady,
      userId: user.id,
      phase
    });
    
    fetch(`${serverPHP}updateUser.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        id: user.id, 
        name: user.name, 
        role: user.role, 
        currentsession: user.currentsession, 
        isready: isReady ? 1 : 0 
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        console.log('📊 User ready status updated:', { isReady, userId: user.id });
      }
    })
    .catch(error => {
      console.error('📊 Failed to update user ready status:', error);
    });
  }, [userEvaluations, currentContributions, user]);

  useEffect(() => {
    checkAllEvaluated();
  }, [userEvaluations, checkAllEvaluated]);

  const isActive = (buttonValeur) => {
    const currentValeur = userEvaluations.get(currentContributions[currentContributionIndex]?.id);
    return currentValeur === buttonValeur;
  };

  const navigateContributions = (direction) => {
    setCurrentContributionIndex(prev => {
      const newIndex = prev + direction;
      return newIndex < 0 ? 0 : newIndex >= currentContributions.length ? currentContributions.length - 1 : newIndex;
    });
  };

  const getMessageInfo = (article, statut, icon) => {
    return (
      <div className="card text-center">
        <div className="text-4xl mb-3">{icon}</div>
        <h3 className="font-semibold mb-3">Accès restreint</h3>
        <p>Vous avez le statut {article}<span className="font-semibold text-primary">{statut}</span>.<br />Vous ne pouvez donc pas participer aux phases d'évaluation.</p>
        <p className="text-sm text-secondary mt-3">Pour changer votre statut, cliquez sur votre nom en haut à gauche.</p>
      </div>
    );
  };

  // Vérifier le rôle de l'utilisateur
  if (user.role === 2) {
    console.log('👁️ User is spectator, showing restricted access message');
    return getMessageInfo('de ', 'spectateur', '👁️');
  }

  const evaluatedCount = Array.from(userEvaluations.values()).filter(value => value !== null).length;
  const totalContributions = currentContributions.length;
  const currentContribution = currentContributions[currentContributionIndex];

  const smileyOptions = [
    { value: -2, emoji: '😡', label: 'Très négatif' },
    { value: -1, emoji: '😕', label: 'Négatif' },
    { value: 5, emoji: '😐', label: 'Neutre' },
    { value: 1, emoji: '🙂', label: 'Positif' },
    { value: 2, emoji: '😊', label: 'Très positif' }
  ];

  if (currentContributions.length === 0) {
    return (
      <div className="card text-center">
        <div className="loading mb-3"></div>
        <p>Chargement des contributions...</p>
      </div>
    );
  }

  console.log('📊 Rendering evaluation interface:', {
    currentContributions: currentContributions.length,
    evaluatedCount,
    totalContributions,
    userRole: user.role
  });

  return (
    <div className="evaluation-container-simplified">
      <div className="evaluation-progress-header">
        <div className="progress-info">
          <h2 className="evaluation-title">⭐ Évaluez les contributions</h2>
          <div className="progress-summary">
            <span className="progress-text">Progression : <span className={`progress-count ${evaluatedCount === totalContributions ? 'complete' : ''}`}>{evaluatedCount}/{totalContributions}</span></span>
            {currentContributions.length > 1 && (<span className="current-item">Contribution {currentContributionIndex + 1}/{currentContributions.length}</span>)}
          </div>
        </div>
      </div>

      <div className="evaluation-main-zone">
        <div className="contribution-card">
          <div className="contribution-header">
            <h3 className="contribution-title">
              {userEvaluations.get(currentContribution?.id) !== null ? (
                <div className="status-evaluated"><span className="status-icon">✅</span><span className="contribution-title">Évaluation enregistrée</span></div>
              ) : (
                <div className="status-pending"><span className="status-icon">⏳</span><span className="contribution-title">En attente de votre évaluation</span></div>
              )}
            </h3>
            {currentContributions.length > 1 && (
              <div className="navigation-buttons">
                <button onClick={() => navigateContributions(-1)} disabled={currentContributionIndex === 0} className="nav-btn nav-prev">← Précédent</button>
                <button onClick={() => navigateContributions(1)} disabled={currentContributionIndex === currentContributions.length - 1} className="nav-btn nav-next">Suivant →</button>
              </div>
            )}
          </div>
          
          <div className="contribution-content"><p className="contribution-text">{currentContribution?.contribution}</p></div>

          <div className="evaluation-actions">
            <p className="evaluation-prompt">Comment évaluez-vous cette contribution ?</p>
            <div className="smiley-rating-simplified">
              {smileyOptions.map((option) => (
                <button key={option.value} onClick={() => handleEvaluation(currentContribution?.id, option.value)} className={`smiley-btn ${isActive(option.value) ? 'selected' : ''}`} title={option.label}>
                  {option.emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>


      {showAllContributions && pastContributions.length > 0 && (
        <div className="previous-contributions">
          <div className="previous-header">
            <h3 className="previous-title">📚 Contributions précédentes retenues</h3>
            <p className="previous-subtitle">Voici un rappel des formulations déjà validées</p>
          </div>
          <div className="previous-content">
            <SyntheseSession sujet={sujet} premieres_contributions={pastContributions} setModalContent={setModalContent} setModalTitle={setModalTitle} setShowModal={setShowModal} setPrefixNext={false} parametres={parametres} type={type} redactionLongue={redactionLongue} redactionLonguePourTop={redactionLongue} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Evaluation;